import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  User,
  Calendar,
  MapPin,
  Clock,
  Activity,
  Book,
  Plus,
  Download,
  Loader,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  CheckCircle,
  XCircle,
  FileText,
  Bell,
  Calendar as CalendarIcon,
  CirclePlay,
  AlertTriangle,
  MessageSquare,
  Stethoscope,
  Clipboard,
  Filter,
} from "lucide-react";
import participantService from "../../services/participantService";
import completedSurveyService from "../../services/completedSurveyService";
import PageHeader from '../Shared/PageHeader';
import dcqService from "../../services/dcqService";
import api from "../../services/api";
import { useMergedSurveys } from '../../hooks/useMergedSurveys';
import { Tab } from "@headlessui/react";
import { AuthContext } from '../../contexts/AuthContext';
import permissionsService from '../../services/permissionsService';
import toast from 'react-hot-toast';
import DateMocker from '../Shared/DateMocker';
import { parseSimulatedDate } from '../../utils/dateUtils';
import {
  canActivateVisits,
  canChangeParticipantVisitActivation,
} from '../../utils/activationPermissions';
import {
  DEFAULT_PARTICIPANT_SURVEY_SORT,
  sortParticipantSurveyRows,
} from '../../utils/participantSurveyTableSorting';
import CaregiversTab from '../Caregivers/CaregiversTab';
import studyService from '../../services/studyService';
import { Users as UsersIcon } from 'lucide-react';
import { ParticipantActiveBadge, SuspendedBadge } from '../common/ParticipantStatusBadge';

const formatScheduleType = (scheduleType) => {
  const scheduleTypeMap = {
    'daily': 'Daily',
    'patient_prompted': 'Patient Prompted',
    'scheduled': 'Scheduled',
    'site_prompted': 'Site Prompted',
    'other': 'Other'
  };
  
  // If the schedule type includes a known type, return the formatted version
  for (const [key, value] of Object.entries(scheduleTypeMap)) {
    if (scheduleType && scheduleType.toLowerCase().includes(key)) {
      return value;
    }
  }
  
  // If not found, return the original or a default
  return scheduleType || 'Unknown';
};

// FECOA-1491: Scheduled surveys are split by delivery location. Only on-site
// scheduled surveys belong in the activatable Visit tables; remote (and any
// non-on-site) scheduled surveys are surfaced in their own non-activatable
// table, mirroring how Dailies and Patient Prompted are shown. This matches
// the backend invariant `is_auto_activated = site_type != 'on-site'`, so the
// only surveys gated by the Activate/Deactivate control are on-site visits.
const ONSITE_SITE_TYPES = ['on-site', 'onsite'];

const getSurveySiteType = (survey) =>
  (survey?.site_type || survey?.metadata?.site_type || '')
    .toString()
    .trim()
    .toLowerCase();

const isOnSiteSurvey = (survey) => ONSITE_SITE_TYPES.includes(getSurveySiteType(survey));

const isOnSiteWindow = (window) =>
  ONSITE_SITE_TYPES.includes(
    (window?.metadata?.site_type || window?.site_type || '')
      .toString()
      .trim()
      .toLowerCase()
  );

const tabs = [
  { id: 'info', label: 'Info', icon: Activity },
  { id: 'dcqs', label: 'DCQs', icon: FileText },
];

const ParticipantDetails = () => {

  const getActiveTabFromURL = () => {
    const location = useLocation();
    if (!location.hash)
      return 0;
    const id = location.hash.substring(1);
    const t = tabs.findIndex(tab => tab.id === id);
    return t;
  }

  const updateActiveTab = (tab) => {
    if (tab >= 0 && tab < allTabs.length) {  // Check bounds and use allTabs instead of tabs
      const tabId = allTabs[tab].id;  // Use allTabs instead of tabs
      if (tabId == 'info') {
        navigate('', { replace: true });
        // Show the toggle on the Info tab
        setShowFutureAssessmentsToggle(true);
      } else {
        navigate(`#${allTabs[tab].id}`, { replace: true });  // Use allTabs here too
        // Hide the toggle when not on the Info tab
        setShowFutureAssessmentsToggle(false);
      }
      setSelectedTab(tab);
    }
  }

  const navigateToCompletedSurvey = (id) => {
    navigate(`/studies/${studyId}/sites/${siteId}/participant/${participantID}/completed-surveys/${id}`)
  }

  const { participantID, studyId, siteId } = useParams();
  const [participantDetails, setParticipantDetails] = useState(null);
  const [lastDataSend, setLastDataSend] = useState("N/A");
  const navigate = useNavigate();
  const location = useLocation();
  const [dcqs, setDcqs] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  // Add a separate loading state for window activation
  const [activationLoading, setActivationLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(getActiveTabFromURL());
  const [expandedSections, setExpandedSections] = useState({
    dailySurveys: true, // Open daily surveys by default
    visitSurveys: true, // Open visit surveys by default
  });
  const [bypassCache, setBypassCache] = useState(true);
  const [simulatedDate, setSimulatedDate] = useState(null);
  const [showFutureAssessments, setShowFutureAssessments] = useState(false);
  const [showFutureAssessmentsToggle, setShowFutureAssessmentsToggle] = useState(true); // Start visible
  
  // When toggle changes, refetch surveys with updated filter
  useEffect(() => {
    console.log('Show future assessments changed to:', showFutureAssessments);
    if (!mergedSurveysLoading) { // Only trigger if initial load is complete
      refetchSurveys(bypassCache, simulatedDate);
    }
  }, [showFutureAssessments, bypassCache, simulatedDate]);
  const { mergedSurveys, loading: mergedSurveysLoading, error: mergedSurveysError, refetch: refetchSurveys } = useMergedSurveys(participantID, simulatedDate);
  const [organizedSurveys, setOrganizedSurveys] = useState({
    dailySurveys: [],
    patientPromptedSurveys: [],
    remoteSurveys: [],
    visitSurveys: []
  });
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [completionStats, setCompletionStats] = useState(null);
  const [allSurveys, setAllSurveys] = useState([]);
  const [allTabs, setTabs] = useState(tabs);
  const [surveySortConfigs, setSurveySortConfigs] = useState({});
  // FECOA-1462: filters for the "All Assessments" tab. Client-side filtering
  // over the already-built `allSurveys` list (status, schedule type, date range).
  const [allSurveyFilters, setAllSurveyFilters] = useState({
    status: 'all',
    scheduleType: 'all',
    dateFrom: '',
    dateTo: '',
  });
  const { isAuthenticated, user, userPermissions } = useContext(AuthContext);
  const canManageVisit = canChangeParticipantVisitActivation(
    user,
    participantDetails?.is_active,
  );
  const hasCheckedMissedSurveys = useRef(false);
  // FECOA-1609: window-generation status (rollup over the participant's schedules).
  // Seeded from the detail payload, then kept live by polling while building.
  const [genStatus, setGenStatus] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const isGenerating = genStatus === 'pending' || genStatus === 'in_progress';
  const generationFailed = genStatus === 'failed';

  const showExportButton = false;

// This useEffect handles survey organization based on their type
useEffect(() => {
  if (mergedSurveys) {
    // Flatten all surveys from all visits
    const surveysFromVisits = mergedSurveys.visitSurveys
      ? mergedSurveys.visitSurveys.flatMap(visit => visit.surveys)
      : [];
    
    // Combine all potential sources of surveys
    const allPotentialSurveys = [
      ...surveysFromVisits,
      ...completedSurveys, // Include completed surveys as they might have daily assessments not in merged surveys
      ...(mergedSurveys.assessments || []) // Include assessments from the new endpoint
    ];
    
    // Filter out future assessments if toggle is off
    const currentDate = simulatedDate ? parseSimulatedDate(simulatedDate) : new Date();
    const currentDateString = currentDate.toISOString().split('T')[0];
    console.log(`Current date for filtering: ${currentDateString}, Show future: ${showFutureAssessments}`);
    
    const filteredSurveys = showFutureAssessments
      ? allPotentialSurveys
      : allPotentialSurveys.filter(survey => {
          // Always include completed surveys regardless of timing
          if (survey.completed_at) return true;
          // Use the window's open time so a window only appears once it has
          // actually opened. Comparing whole-date strings in UTC mishandles
          // participants in non-UTC timezones — it can let tomorrow's local
          // window slip through after UTC midnight but before local midnight.
          const dateStr = survey.available_at || survey.start_time || survey.date;
          if (!dateStr) return true;
          return new Date(dateStr) <= currentDate;
        });
    
    // Remove duplicates based on id/object_id
    // For patient-prompted surveys, use a unique key that includes the CompletedSurvey ID
    // to allow multiple completions of the same survey to be shown
    let patientPromptedIndex = 0;
    const uniqueSurveys = Array.from(new Map(
      filteredSurveys
        .filter(survey => survey) // Filter out any undefined items
        .map(survey => {
          // Check if this is a patient-prompted survey using all possible indicators
          const isPatientPrompted = 
            survey.isPatientPrompted === true ||
            survey.schedule_type?.toLowerCase().includes('patient_prompted') ||
            survey.metadata?.schedule_type?.toLowerCase().includes('patient_prompted') ||
            survey.visit_type === 'patient_prompted' ||
            survey.metadata?.visit_type === 'patient_prompted' ||
            survey.response_data?.metadata?.visit_type === 'patient_prompted';
          
          // For patient-prompted surveys, use an incrementing index to guarantee uniqueness
          // This ensures each completion is shown separately even if they have the same id/completed_at
          let uniqueKey;
          if (isPatientPrompted) {
            uniqueKey = `patient_prompted_${patientPromptedIndex++}_${survey.id || survey.object_id}_${survey.completed_at || ''}`;
          } else {
            uniqueKey = survey.id || survey.object_id;
          }
          
          return [uniqueKey, survey];
        })
    ).values());
    
    // Create separate arrays for different survey types
    const dailySurveys = [];
    const patientPromptedSurveys = [];
    const scheduledSurveys = [];
    // FECOA-1491: remote (non on-site) scheduled surveys get their own table.
    const remoteSurveys = [];
    
    // Create sets to track which surveys are already categorized
    const categorizedSurveyIds = new Set();
    
    // Categorize each survey based on its metadata and type
    uniqueSurveys.forEach(survey => {
      // Get schedule type from metadata if available
      const metadataScheduleType = survey.metadata?.schedule_type;
      const visitType = survey.metadata?.visit_type || survey.visit_type;
      
      // Determine the effective schedule type
      const effectiveScheduleType = metadataScheduleType || survey.schedule_type;
      
      // Process and add survey to appropriate array
      const processedSurvey = {
        ...survey,
        survey_name: survey.survey_name || 
                    (survey.survey_details && survey.survey_details.name) || 
                    (survey.response_data && survey.response_data.metadata && survey.response_data.metadata.survey_name) ||
                    'Unnamed Survey',
        relevantDate: survey.status === 'completed' && survey.completed_at ? survey.completed_at : 
                      (survey.status === 'missed' ? 
                        // For missed surveys, use any available date field
                        (survey.date || survey.start_time || survey.available_at || survey.response_data?.metadata?.missed_at || survey.created || survey.metadata?.created) : 
                        // For other statuses (open, pending, etc)
                        (survey.date || survey.start_time || survey.available_at || survey.created || survey.metadata?.created))
      };
      
      const surveyId = survey.id || survey.object_id;
      
      // Check if this is a patient-prompted survey using all possible indicators
      const isPatientPromptedSurvey = 
        survey.isPatientPrompted === true ||
        effectiveScheduleType?.toLowerCase().includes('patient_prompted') || 
        visitType === 'patient_prompted' ||
        survey.response_data?.metadata?.visit_type === 'patient_prompted';
      
      // For patient-prompted surveys, use a unique key that includes completion time
      // This allows multiple completions of the same survey to be shown
      const trackingKey = isPatientPromptedSurvey 
        ? `${surveyId}_${survey.completed_at || survey.created || Math.random()}` 
        : surveyId;
      
      // Classify based on schedule type from metadata or visit_type
      if ((effectiveScheduleType?.toLowerCase() === 'daily' || visitType === 'daily') && 
          !categorizedSurveyIds.has(surveyId)) {
        dailySurveys.push(processedSurvey);
        categorizedSurveyIds.add(surveyId);
      } 
      else if (isPatientPromptedSurvey) {
        // Patient-prompted surveys: always add (don't check categorizedSurveyIds)
        // Each completion should be shown separately even if they have the same id/completed_at
        patientPromptedSurveys.push(processedSurvey);
        // Don't add to categorizedSurveyIds - allow duplicates for patient-prompted
      } 
      else if ((effectiveScheduleType?.toLowerCase().includes('scheduled') || 
                visitType === 'scheduled' ||
                survey.visit_id !== 'unassigned') && 
               !categorizedSurveyIds.has(surveyId)) {
        // FECOA-1491: only on-site scheduled surveys go to the activatable
        // Visit tables. Remote (and any non on-site) scheduled surveys are
        // surfaced in their own table, just like Dailies / Patient Prompted.
        if (isOnSiteSurvey(survey)) {
          scheduledSurveys.push(processedSurvey);
        } else {
          remoteSurveys.push(processedSurvey);
        }
        categorizedSurveyIds.add(surveyId);
      } 
      else if (!categorizedSurveyIds.has(surveyId)) {
        // Default for anything else - check if it's unassigned or has a visit
        if (survey.visit_id && survey.visit_id !== 'unassigned') {
          // Has a real visit, so it behaves like a scheduled survey — keep the
          // on-site vs remote split consistent with the branch above.
          if (isOnSiteSurvey(survey)) {
            scheduledSurveys.push(processedSurvey);
          } else {
            remoteSurveys.push(processedSurvey);
          }
        } else {
          // If truly unassigned, put in patient prompted by default
          patientPromptedSurveys.push(processedSurvey);
        }
        categorizedSurveyIds.add(surveyId);
      }
    });
    
    // Sort each category by date (most recent first)
    const sortByDate = (a, b) => {
      if (!a.relevantDate && !b.relevantDate) return 0;
      if (!a.relevantDate) return 1;  // Push items without dates to the end
      if (!b.relevantDate) return -1; // Push items without dates to the end
      return new Date(b.relevantDate) - new Date(a.relevantDate);
    };
    
    dailySurveys.sort(sortByDate);
    patientPromptedSurveys.sort(sortByDate);
    scheduledSurveys.sort(sortByDate);
    remoteSurveys.sort(sortByDate);
    
    console.log('Daily surveys found (total):', dailySurveys.length, dailySurveys);
    console.log('Patient prompted surveys found:', patientPromptedSurveys.length, patientPromptedSurveys);
    console.log('Scheduled (on-site) surveys found:', scheduledSurveys.length, scheduledSurveys);
    console.log('Remote surveys found:', remoteSurveys.length, remoteSurveys);
    
    // Group scheduled surveys by visit ID
    const visitGroups = {};
    scheduledSurveys.forEach(survey => {
      // Extract visit ID from all possible fields and formats
      // The all-assessments endpoint returns visit as "Visit X", so handle that format too
      let visitId = 'unassigned';
      
      if (survey.visit_id) {
        visitId = survey.visit_id;
      } else if (survey.metadata?.visit_id) {
        visitId = survey.metadata.visit_id;
      } else if (survey.visit_number) {
        visitId = survey.visit_number;
      } else if (survey.visit && typeof survey.visit === 'string') {
        // Handle "Visit X" format
        const visitMatch = survey.visit.match(/Visit\s+(\d+|unassigned)/i);
        if (visitMatch) {
          visitId = visitMatch[1];
        }
      }
      if (!visitGroups[visitId]) {
        visitGroups[visitId] = {
          id: visitId,
          name: visitId === 'unassigned' ? 'Unassigned Visits' : `Visit ${visitId}`,
          surveys: [],
          completedCount: 0,
          totalCount: 0
        };
      }
      
      visitGroups[visitId].surveys.push(survey);
      visitGroups[visitId].totalCount++;
      if (survey.status === 'completed') {
        visitGroups[visitId].completedCount++;
      }
    });
    
    // Convert visit groups to array and add isActivated flag
    const visitSurveys = Object.values(visitGroups).map(group => {
      // Check if any survey in the group is ready for activation
      const hasActivatableSurveys = group.surveys.some(survey => 
        survey.status === 'open' || 
        (survey.status !== 'completed' && survey.status !== 'missed')
      );
      
      // Process surveys to store original status
      const processedSurveys = group.surveys.map(survey => ({
        ...survey,
        original_status: survey.original_status || survey.status,
        status: survey.is_activated ? "Activated" : survey.status
      }));
      
      return {
        ...group,
        isActivated: processedSurveys.some(s => s.is_activated),
        canActivate: hasActivatableSurveys,
        surveys: processedSurveys
      };
    });
    // Collect all surveys in one list for the All Assessments tab. This list is
    // intentionally built from the unfiltered set so the tab always reflects
    // past, current, AND upcoming windows regardless of the Info-tab toggle.
    let allSurveysCollection = [];
    const seenAllSurveyKeys = new Set();
    let allTabPatientPromptedIndex = 0;

    allPotentialSurveys
      .filter(survey => survey)
      .forEach(survey => {
        const metadataScheduleType = survey.metadata?.schedule_type;
        const visitType = survey.metadata?.visit_type || survey.visit_type;
        const effectiveScheduleType = metadataScheduleType || survey.schedule_type;

        const isPatientPromptedSurvey =
          survey.isPatientPrompted === true ||
          effectiveScheduleType?.toLowerCase().includes('patient_prompted') ||
          visitType === 'patient_prompted' ||
          survey.response_data?.metadata?.visit_type === 'patient_prompted';

        const surveyId = survey.id || survey.object_id;
        const dedupeKey = isPatientPromptedSurvey
          ? `pp_${allTabPatientPromptedIndex++}_${surveyId}_${survey.completed_at || ''}`
          : surveyId;
        if (seenAllSurveyKeys.has(dedupeKey)) return;
        seenAllSurveyKeys.add(dedupeKey);

        let surveyType = 'Other';
        let scheduleType = 'Unknown';
        if (effectiveScheduleType?.toLowerCase() === 'daily' || visitType === 'daily') {
          surveyType = 'Daily';
          scheduleType = 'Daily';
        } else if (isPatientPromptedSurvey) {
          surveyType = 'Patient Prompted';
          scheduleType = 'Patient Prompted';
        } else if (effectiveScheduleType?.toLowerCase().includes('scheduled') || visitType === 'scheduled') {
          surveyType = 'Visit';
          scheduleType = 'Scheduled';
        }

        const visitId = survey.visit_id || survey.metadata?.visit_id || 'unassigned';
        const visitName = visitId === 'unassigned' ? 'Unassigned' : `Visit ${visitId}`;

        const relevantDate = survey.status === 'completed' && survey.completed_at
          ? survey.completed_at
          : (survey.date || survey.start_time || survey.available_at || survey.created || survey.metadata?.created);

        allSurveysCollection.push({
          ...survey,
          survey_name: survey.survey_name ||
            (survey.survey_details && survey.survey_details.name) ||
            (survey.response_data && survey.response_data.metadata && survey.response_data.metadata.survey_name) ||
            'Unnamed Survey',
          relevantDate,
          scheduleType,
          surveyType,
          visitName,
        });
      });

    // Sort by date (most recent first)
    allSurveysCollection.sort(sortByDate);
    
    // Update the state
    setAllSurveys(allSurveysCollection);
    setOrganizedSurveys({ 
      dailySurveys,
      patientPromptedSurveys, 
      remoteSurveys,
      visitSurveys 
    });
    
    // Auto-expand sections
    setExpandedSections(prevState => {
      // Add these new sections if they don't exist
      const newSections = { ...prevState };
      
      if (patientPromptedSurveys.length > 0 && !prevState.hasOwnProperty('patientPromptedSurveys')) {
        newSections.patientPromptedSurveys = true;
      }

      if (remoteSurveys.length > 0 && !prevState.hasOwnProperty('remoteSurveys')) {
        newSections.remoteSurveys = true;
      }
      
      // Add visit IDs to expanded sections if they don't exist
      visitSurveys.forEach(visit => {
        if (!prevState.hasOwnProperty(visit.id)) {
          newSections[visit.id] = true;
        }
      });
      
      return newSections;
    });
  }
}, [mergedSurveys, completedSurveys]);

// Effect for adding All Surveys tab for internal users
useEffect(() => {
    if (isAuthenticated && user?.is_internal) {
      setTabs(prevTabs => {
        // Check if the tab already exists to avoid duplicates
        if (!prevTabs.some(tab => tab.id === 'all-surveys')) {
          return [...prevTabs, { id: 'all-surveys', label: 'All Assessments', icon: Clipboard }];
        }
        return prevTabs;
      });
    }
  }, [isAuthenticated, user]);

  // FECOA-1609: seed generation status from the detail payload once it loads.
  useEffect(() => {
    if (participantDetails?.generation_status) {
      setGenStatus(participantDetails.generation_status);
    }
  }, [participantDetails?.generation_status]);

  // FECOA-1609: while windows are still generating, poll the live status endpoint
  // and auto-re-enable the gated sections once the participant becomes ready.
  useEffect(() => {
    if (!participantID || !isGenerating) return;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const data = await participantService.getGenerationStatus({ participantID });
        if (cancelled) return;
        const entry = (data.participants || [])[0];
        if (entry) setGenStatus(entry.status);
        if (entry && (entry.status === 'pending' || entry.status === 'in_progress')) {
          timer = setTimeout(poll, 5000);
        }
      } catch {
        if (!cancelled) timer = setTimeout(poll, 10000);
      }
    };

    timer = setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [participantID, isGenerating]);

  // FECOA-1609: Study Coordinator can clear a stuck/failed generation state.
  const handleMarkGenerationComplete = async () => {
    setMarkingComplete(true);
    try {
      await participantService.markGenerationComplete(participantID);
      setGenStatus('complete');
      toast.success('Survey setup marked complete');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to mark setup complete');
    } finally {
      setMarkingComplete(false);
    }
  };

  // Gate the "Mark complete" button on the same permission the backend enforces
  // (mark-complete endpoint requires can_change_participant). Role alone is not
  // enough — a user with the role but without the permission would 403 on click.
  const [canMarkGenerationComplete, setCanMarkGenerationComplete] = useState(false);
  useEffect(() => {
    let active = true;
    permissionsService
      .hasPermission('can_change_participant')
      .then((ok) => { if (active) setCanMarkGenerationComplete(!!ok); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // FECOA-1321: only show Caregivers tab when the active study has allow_caregivers=True
  const [showCaregiversTab, setShowCaregiversTab] = useState(false);
  useEffect(() => {
    if (!studyId) return;
    let cancelled = false;
    studyService
      .getSingleStudy(studyId)
      .then((study) => {
        if (!cancelled) setShowCaregiversTab(!!study?.allow_caregivers);
      })
      .catch(() => {
        if (!cancelled) setShowCaregiversTab(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studyId]);

  useEffect(() => {
    if (showCaregiversTab) {
      setTabs(prev => {
        if (prev.some(t => t.id === 'caregivers')) return prev;
        return [...prev, { id: 'caregivers', label: 'Caregivers', icon: UsersIcon }];
      });
    } else {
      setTabs(prev => prev.filter(t => t.id !== 'caregivers'));
    }
  }, [showCaregiversTab]);

  // Function to handle activation/deactivation of visit windows
  const handleVisitWindowActivation = async (visitId, activate) => {
    try {
      if (!participantDetails?.is_active) {
        toast.error('This participant is inactive. Their assessments are suspended.');
        return;
      }

      // Check user permissions for visit activation
      if (!canActivateVisits(user)) {
        toast.error('You do not have permission to activate/deactivate survey windows.');
        return;
      }
      
      // Show loading state
      setActivationLoading(true);
      
      if (!participantDetails || !participantDetails.id) {
        throw new Error("Participant details not available");
      }

      console.log(`Activating visit ${visitId} for participant ${participantDetails.id}, activate=${activate}`);
      
      // First, fetch all available windows for this participant
      const allSurveysEndpoint = `/api/participants/${participantDetails.id}/all-surveys-for-activation/`;
      console.log('Fetching all available windows:', allSurveysEndpoint);
      
      let surveyData;
      try {
        let surveyResponse;
        try {
          surveyResponse = await api.get(allSurveysEndpoint);
        } catch (axiosError) {
          const errorText = axiosError.response?.data?.detail || axiosError.message;
          throw new Error(`Failed to fetch survey windows: ${axiosError.response?.status || ''} - ${errorText}`);
        }
        const responseData = surveyResponse.data;
        console.log('Raw response data:', responseData);
        surveyData = responseData || [];
      } catch (error) {
        console.error('Error fetching survey data:', error);
        throw new Error(`Failed to fetch survey windows: ${error.message}`);
      }
      
      console.log('Available windows:', surveyData);
      
      // Make sure surveyData is an array
      if (!Array.isArray(surveyData)) {
        if (surveyData && typeof surveyData === 'object') {
          // Maybe it's an object with a data property or similar
          if (Array.isArray(surveyData.visits)) {
            // Using the visits array from the grouped response
            surveyData = surveyData.visits.find(v => v.visit_id === visitId || v.name?.includes(visitId))?.windows || [];
          } else if (Array.isArray(surveyData.windows)) {
            surveyData = surveyData.windows;
          } else {
            surveyData = [];
          }
        } else {
          surveyData = [];
        }
      }
      
      // Filter windows that match our visit_id
      const windowsForVisit = surveyData.filter(window => {
        // Log each window and why it's being filtered (for debugging)
        console.log('Evaluating window for activation:', window);
        
        const matchesVisitId = 
          (window.visit_id === visitId || window.visit_id === String(visitId)) ||
          (window.visit_number === visitId || window.visit_number === String(visitId)) ||
          (window.visit && window.visit.includes(`Visit ${visitId}`)) ||
          (window.metadata && (
            window.metadata.visit_id === visitId || 
            window.metadata.visit_id === String(visitId) ||
            window.metadata.visit_number === visitId ||
            window.metadata.visit_number === String(visitId)
          ));
        
        // FECOA-1491: the Visit tables only contain on-site scheduled surveys,
        // so activation must never touch a remote window that merely shares a
        // visit number. Restrict the activation set to on-site windows.
        const matchesOnSiteVisit = matchesVisitId && isOnSiteWindow(window);

        if (matchesOnSiteVisit) {
          console.log('MATCH: On-site window matches visit ID', visitId, window.id);
        }

        return matchesOnSiteVisit;
      });
      
      console.log('Windows for this visit:', windowsForVisit);
      
      if (windowsForVisit.length === 0) {
        // If we couldn't find any windows by visit ID, we can try a direct API call
        // to the visit-specific endpoint as a fallback
        console.log('No windows found with filtering. Trying direct visit endpoint as fallback.');
        
        try {
          const visitEndpoint = `/api/participants/${participantDetails.id}/visits/${visitId}/${activate ? 'activate' : 'deactivate'}/`;
          console.log('Trying fallback endpoint:', visitEndpoint);
          
          let visitResponse;
          try {
            visitResponse = await api.post(visitEndpoint, { simulated_date: simulatedDate });
          } catch (axiosError) {
            const errorText = axiosError.response?.data?.detail || axiosError.message;
            throw new Error(`Fallback request failed: ${axiosError.response?.status || ''} - ${errorText}`);
          }
          // If we get here, the fallback succeeded
          console.log('Fallback request succeeded');
          
          // Update the UI state and return early
          updateUIState(visitId, activate);
          return;
        } catch (fallbackError) {
          console.error('Fallback request failed:', fallbackError);
          // Continue with the main approach
        }
        
        // If both approaches failed, get all window IDs as a last resort
        console.log('Using all available windows as last resort');
        if (surveyData.length === 0) {
          throw new Error(`No windows found for this participant`);
        }
      }
      
      // Use either the filtered windows or, as a last resort, all available
      // windows. FECOA-1491: even the last-resort set is restricted to on-site
      // windows so activation can never reach remote surveys.
      const windowsToActivate = windowsForVisit.length > 0
        ? windowsForVisit
        : surveyData.filter(isOnSiteWindow);
      
      // Extract window IDs
      const windowIds = windowsToActivate.map(window => {
        // Log each window being activated with its details
        console.log(`Adding window to activation list: ID=${window.id}, Survey ID=${window.survey_id}, Site type=${window.metadata?.site_type}`);
        return window.id;
      });
      
      console.log('Window IDs to activate/deactivate:', windowIds);
      
      if (windowIds.length === 0) {
        throw new Error('No window IDs found to activate/deactivate');
      }
      
      // Now make the activation request with the window_ids parameter
      const activationEndpoint = `/api/participants/${participantDetails.id}/activate-assignments/`;
      console.log('Calling activation endpoint:', activationEndpoint);
      
      const requestBody = {
        window_ids: windowIds,
        activate: activate,
        override_timing: true // To bypass time constraints if needed
      };
      
      console.log('Request body:', requestBody);
      
      try {
        let response;
        try {
          response = await api.post(activationEndpoint, requestBody);
        } catch (axiosError) {
          const errorText = axiosError.response?.data?.detail || axiosError.message;
          throw new Error(`Server error (${axiosError.response?.status || ''}): ${errorText}`);
        }
        console.log('Response status:', response.status);
        const responseBody = response.data;
        console.log('Success response:', responseBody);
      } catch (activationError) {
        console.error('Activation request failed:', activationError);
        throw activationError;
      }
      
      // Update the UI state
      updateUIState(visitId, activate);
      
      // Show success notification
      toast.success(`Visit successfully ${activate ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error(`Error ${activate ? 'activating' : 'deactivating'} visit windows:`, error);
      toast.error(`Failed to ${activate ? 'activate' : 'deactivate'} visit windows: ${error.message}`);
    } finally {
      // Hide loading state
      setActivationLoading(false);
    }
  };
  
  // Helper function to update the UI state after activation/deactivation
  const updateUIState = (visitId, activate) => {
    // Update the local state to reflect the change
    setOrganizedSurveys(prevState => ({
      ...prevState,
      visitSurveys: prevState.visitSurveys.map(visit => {
        if (visit.id === visitId) {
          // Update the visit's activation status
          const updatedVisit = {
            ...visit,
            isActivated: activate
          };
          
          // Update the status of individual surveys
          if (updatedVisit.surveys) {
            updatedVisit.surveys = updatedVisit.surveys.map(survey => ({
              ...survey,
              status: activate ? "Activated" : survey.original_status || survey.status
            }));
          }
          
          return updatedVisit;
        }
        return visit;
      })
    }));

    // If refetchSurveys exists, call it
    if (typeof refetchSurveys === 'function') {
      refetchSurveys(bypassCache, simulatedDate);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getSurveySortConfig = (tableId) =>
    surveySortConfigs[tableId] || DEFAULT_PARTICIPANT_SURVEY_SORT;

  const handleSurveySort = (tableId, key) => {
    setSurveySortConfigs(prev => {
      const current = prev[tableId] || DEFAULT_PARTICIPANT_SURVEY_SORT;
      const isSameColumn = current.key === key;
      const nextDirection = isSameColumn && current.direction === 'ascending'
        ? 'descending'
        : 'ascending';

      return {
        ...prev,
        [tableId]: {
          key,
          direction: isSameColumn ? nextDirection : 'ascending',
        },
      };
    });
  };

  const getSortedSurveyRows = (tableId, surveys) =>
    sortParticipantSurveyRows(surveys, getSurveySortConfig(tableId));

  // FECOA-1462: Status / Schedule Type options for the All Assessments filter
  // bar, derived from the data so we never show an option that matches nothing.
  const allSurveyStatusOptions = useMemo(() => {
    const seen = new Map();
    allSurveys.forEach((s) => {
      const raw = (s.status || '').toString().trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, raw.charAt(0).toUpperCase() + raw.slice(1));
      }
    });
    return Array.from(seen, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allSurveys]);

  const allSurveyScheduleTypeOptions = useMemo(() => {
    const seen = new Set();
    allSurveys.forEach((s) => {
      if (s.scheduleType && s.scheduleType !== 'Unknown') seen.add(s.scheduleType);
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [allSurveys]);

  // FECOA-1462: apply the active filters before sorting/rendering.
  const filteredAllSurveys = useMemo(() => {
    const { status, scheduleType, dateFrom, dateTo } = allSurveyFilters;
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;

    return allSurveys.filter((survey) => {
      if (status !== 'all' && (survey.status || '').toString().toLowerCase() !== status) {
        return false;
      }
      if (scheduleType !== 'all' && survey.scheduleType !== scheduleType) {
        return false;
      }
      if (fromTs !== null || toTs !== null) {
        const rowDate = survey.relevantDate || survey.completed_at || survey.available_at || survey.date;
        const rowTs = rowDate ? new Date(rowDate).getTime() : NaN;
        if (Number.isNaN(rowTs)) return false;
        if (fromTs !== null && rowTs < fromTs) return false;
        if (toTs !== null && rowTs > toTs) return false;
      }
      return true;
    });
  }, [allSurveys, allSurveyFilters]);

  const hasActiveAllSurveyFilters =
    allSurveyFilters.status !== 'all' ||
    allSurveyFilters.scheduleType !== 'all' ||
    allSurveyFilters.dateFrom !== '' ||
    allSurveyFilters.dateTo !== '';

  const resetAllSurveyFilters = () =>
    setAllSurveyFilters({ status: 'all', scheduleType: 'all', dateFrom: '', dateTo: '' });

  const renderSortableHeader = (tableId, key, label, className = "px-3 py-2") => {
    const sortConfig = getSurveySortConfig(tableId);
    const isActive = sortConfig.key === key;
    const SortIcon = isActive
      ? sortConfig.direction === 'ascending'
        ? ChevronUp
        : ChevronDown
      : ChevronsUpDown;

    const ariaSort = isActive
      ? sortConfig.direction === 'ascending'
        ? 'ascending'
        : 'descending'
      : 'none';

    return (
      <th
        scope="col"
        aria-sort={ariaSort}
        className={`${className} text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}
      >
        <button
          type="button"
          onClick={() => handleSurveySort(tableId, key)}
          className="inline-flex items-center gap-1 rounded hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <span>{label}</span>
          <SortIcon className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </th>
    );
  };

  useEffect(() => {
    console.log('Expanded sections state:', expandedSections);
    console.log('Visit surveys available:', organizedSurveys.visitSurveys);
    
    // Auto-expand visit sections when data is loaded
    if (organizedSurveys.visitSurveys && organizedSurveys.visitSurveys.length > 0) {
      // Create a new object to hold potential updated state
      const newExpandedSections = { ...expandedSections };
      
      // Track if we made any changes
      let madeChanges = false;
      
      organizedSurveys.visitSurveys.forEach(visit => {
        // Only update if this key doesn't already exist or is false
        if (!expandedSections[visit.id]) {
          newExpandedSections[visit.id] = true;
          madeChanges = true;
        }
      });
      
      // Only update state if we actually made changes
      if (madeChanges) {
        console.log('Setting expanded sections:', newExpandedSections);
        setExpandedSections(newExpandedSections);
      }
    }
  }, [organizedSurveys.visitSurveys]); // Removed expandedSections from dependencies

  const SurveyTypeIcon = ({ type, survey }) => {
    // First handle the Patient Prompted type explicitly
    if (survey && (
      (survey.metadata && survey.metadata.visit_type === 'patient_prompted') ||
      survey.visit_type === 'patient_prompted' ||
      (type && type.toLowerCase().includes('patient_prompted'))
    )) {
      return <Bell className="w-4 h-4 text-purple-500" />;
    }
    
    // Then check if it's a daily assessment
    if (survey && (
      (survey.metadata && survey.metadata.visit_type === 'daily') ||
      survey.visit_type === 'daily' ||
      (type && type.toLowerCase().includes('daily'))
    )) {
      return <CalendarIcon className="w-4 h-4 text-blue-500" />;
    }
  
    // Then check if it's a visit/scheduled survey
    if (survey && (
      (survey.metadata && (
        survey.metadata.visit_type === 'scheduled' || 
        survey.metadata.visit_id && survey.metadata.visit_id !== 'unassigned'
      )) ||
      survey.visit_type === 'scheduled' ||
      survey.visit_id && survey.visit_id !== 'unassigned' ||
      (type && type.toLowerCase().includes('scheduled'))
    )) {
      return <Calendar className="w-4 h-4 text-green-500" />;
    }
  
    // Fallback to icon detection based on type string
    const lowerType = typeof type === 'string' ? type.toLowerCase() : '';
    
    if (lowerType.includes('daily')) {
      return <CalendarIcon className="w-4 h-4 text-blue-500" />;
    } else if (lowerType.includes('patient_prompted') || lowerType.includes('patient prompted')) {
      return <Bell className="w-4 h-4 text-purple-500" />;
    } else if (lowerType.includes('timer')) {
      return <Clock className="w-4 h-4 text-orange-500" />;
    } else if (lowerType.includes('scheduled')) {
      return <Calendar className="w-4 h-4 text-green-500" />;
    } else if (lowerType.includes('site_prompted') || lowerType.includes('site prompted')) {
      return <Stethoscope className="w-4 h-4 text-orange-500" />;
    } else {
      return <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  
  const SurveyStatus = ({ status, isVisitSurvey = false, isSuspended = false }) => {
    // Priority 1: Show completed status (always show if completed)
    if (status === "completed") {
      return (
        <div className="flex items-center">
          <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
          <span className="text-sm text-green-600">Completed</span>
        </div>
      );
    }
    
    // Priority 2: Show suspended status (for incomplete surveys when participant is inactive)
    // FECOA-1885: the shared badge, not a local gray pill — this table was the
    // last surface still rendering Suspended in a different tone from the
    // assessment detail page and the participant profile.
    if (isSuspended || status === "suspended") {
      return <SuspendedBadge />;
    }
    
    // Priority 3: Other statuses
    if (status === "missed") {
      return (
        <div className="flex items-center">
          <XCircle className="w-4 h-4 text-red-500 mr-1" />
          <span className="text-sm text-red-600">Missed</span>
        </div>
      );
    } else if (status === "Activated") {
      return (
        <div className="flex items-center">
          <Bell className="w-4 h-4 text-blue-500 mr-1" />
          <span className="text-sm text-blue-600">Activated</span>
        </div>
      );
    } else if (status === "open" && isVisitSurvey) {
      // Only show "Ready for activation" for visit surveys
      return (
        <div className="flex items-center">
          <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
          <span className="text-sm text-yellow-600">Ready for activation</span>
        </div>
      );
    } else if (status === "open") {
      // For non-visit surveys with "open" status
      return (
        <div className="flex items-center">
          <Bell className="w-4 h-4 text-blue-500 mr-1" />
          <span className="text-sm text-blue-600">Open</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center">
          <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
          <span className="text-sm text-yellow-600">{status}</span>
        </div>
      );  
    }
  };

// Handler for DateMocker component
const applyFakeDate = async (bypassCache, fake_date) => {
  setLoading(true);
  
  try {
    // Reset the current state to avoid stale data
    setOrganizedSurveys({
      dailySurveys: [],
      patientPromptedSurveys: [], // Add this line
      visitSurveys: []
    });
    
    // Apply the new simulated date
    setSimulatedDate(fake_date);
    
    // Force a complete refresh of data with the new date
    await refetchSurveys(true, fake_date);
    
    // Refetch participant details as well to ensure consistency
    await fetchData();
    
    toast.success(`Date simulation applied: ${fake_date}`);
  } catch (err) {
    console.error("Error applying fake date:", err);
    toast.error("Failed to simulate date. Please try again.");
  } finally {
    setLoading(false);
  }
};

// Function to reset date simulation
const resetDateSimulation = async () => {
  setLoading(true);
  
  try {
    // Clear the simulated date
    setSimulatedDate(null);
    
    // Reset survey data
    setOrganizedSurveys({
      dailySurveys: [],
      patientPromptedSurveys: [], // Add this line
      visitSurveys: []
    });
    
    // Force a complete refresh with no date simulation
    await refetchSurveys(true, null);
    
    // Refetch participant details as well
    await fetchData();
    
    toast.success("Date simulation reset to current date");
  } catch (err) {
    console.error("Error resetting simulation:", err);
    toast.error("Failed to reset simulation. Please try again.");
  } finally {
    setLoading(false);
  }
};

  const fetchData = async () => {
    try {
      setLoading(true);

      try {
        const [participantData, dcqsData, lastDataSendData] =
          await Promise.all([
            participantService.getParticipant(participantID),
            dcqService.getDCQsForParticipant(participantID),
            participantService.getParticipantLastDataSend(participantID),
          ]);

        console.log('Participant Data:', participantData);
        // Handle both old and new API response formats
        const participant = participantData.participant || participantData;
        console.log('Processed Participant:', participant);
        
        setParticipantDetails(participant);
        setDcqs(dcqsData);
        setLastDataSend(lastDataSendData.last_data_send);
        

        // MARK: Missed Surveys
        // Check for missed surveys as soon as we have participant data
        // This runs in the background and doesn't block the UI
        // Only check for missed surveys once per component mount
        if (!hasCheckedMissedSurveys.current) {
          hasCheckedMissedSurveys.current = true;
          participantService.checkMissedSurveys(participantID)
            .then(result => {
              if (result.missed_surveys_created && result.missed_surveys_created > 0) {
                console.log(`Created ${result.missed_surveys_created} missed survey records`);
                toast.success(`Created ${result.missed_surveys_created} missed survey records`);
                // No need to manually refresh surveys, useMergedSurveys will auto-refresh on participantID change
              }
            })
            .catch(err => {
              // It's ok if this fails
              console.warn('Failed to check missed surveys:', err);
              console.error("Error checking for missed surveys:", err);
            });
        }

          // MARK: Completed Surveys
          // Fetch completed surveys data after we have the participant details
          try {
            const [statsData] = await Promise.all([
              completedSurveyService.getCompletionStats(participantID)
            ]);
            
            setCompletedSurveys([]);
            setCompletionStats(statsData);
          } catch (surveyError) {
            console.error("Error fetching surveys:", surveyError);
            setError("An error occurred while fetching survey data.");
          }
        
        setError("");
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setError("You do not have permissions to view this page.");
        } else {
          setError("An error occurred while fetching the data.");
        }
      }
    } catch (error) {
        console.error("Error fetching data:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError("You do not have permissions to view this page.");
      } else {
        setError("An error occurred while fetching the data.");
      }
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [participantID]);

  const handleBack = () => {
    // Just remove the participant part from the URL to go back to the site
    const url = window.location.pathname;
    const siteUrl = url.substring(0, url.indexOf('/participant'));
    navigate(siteUrl);
    console.log(`Navigating back to: ${siteUrl}`);
  };

  const handleCreateDCQ = () => {
    // Pass both the UUID (for backend) and human-readable ID (for display)
    navigate(`/studies/${participantDetails.site.study.external_id}/sites/${participantDetails.site.external_id}/participant/${participantDetails.id}/create_dcq`);
  };

  const handleExportDCQs = () => {
    const headers = ["DCQ#", "Type", "Action", "Status", "Last Updated"];
    const csvContent = [
      headers.join(","),
      ...dcqs.map((dcq) =>
        [
          dcq.id,
          dcq.type || "N/A",
          dcq.action || "N/A",
          dcq.status || "N/A",
          formatDate(dcq.updated_at),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `DCQs_Participant_${participantDetails.participant_id ? participantDetails.participant_id.split('-')[1] : 'N/A'}.csv`
    );
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date
      .toLocaleString("en-US", { month: "short" })
      .toUpperCase();
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  const InfoItem = ({ icon: Icon, label, value }) => {
    // Process display value for objects without excessive logging
    let displayValue = value;
    if (value && typeof value === 'object') {
        if (value.site_name) {
            displayValue = value.site_name;
        } else if (value.name) {
            displayValue = value.name;
        } else {
            displayValue = 'N/A';
        }
    }
    
    return (
        <div className="flex items-center space-x-3 py-2">
            <Icon className="w-5 h-5 text-primary-600" />
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}:</span>
            <span className="text-sm text-gray-900 dark:text-gray-100">
                {displayValue ?? "N/A"}
            </span>
        </div>
    );
  };

  const formatParticipantNumber = (id) => {
    if (!id) return "";

    if (typeof id === "string" && id.includes("-")) {
      return id.split("-")[1];
    }

    const stringNumber = String(id);
    return stringNumber.padStart(4, "0");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl dark:shadow-gray-900/20 max-w-md w-full mx-4">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-700 dark:text-gray-200 mb-6">{error}</p>
          <button
            onClick={handleBack}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Ok
          </button>
        </div>
      </div>
    );
  }
  if (!participantDetails) {
    return null;
  }

  // MARK: Render

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <button
            onClick={handleBack}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Site Details</span>
          </button>
          <PageHeader
            icon={User}
            title={`Participant ${formatParticipantNumber(participantDetails?.participant_id || participantDetails?.participant_number)}`}
            titleAccessory={
              <span className="inline-flex items-center gap-2">
                <ParticipantActiveBadge isActive={!!participantDetails?.is_active} />
                {isGenerating && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-500/10 text-warning-600 dark:text-warning-400 border border-warning-500/30">
                    <Loader className="w-3 h-3 animate-spin" /> Building…
                  </span>
                )}
                {generationFailed && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-500/10 text-danger-500 border border-danger-500/30">
                    <AlertTriangle className="w-3 h-3" /> Setup failed
                  </span>
                )}
              </span>
            }
            subtitle="View participant details, surveys, and data queries"
          >
            {(Array.isArray(userPermissions) 
              ? userPermissions.includes('can_file_dcq') 
              : userPermissions?.can_file_dcq) && (
              <button
                onClick={handleCreateDCQ}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create DCQ
              </button>
            )}
          </PageHeader>
        </motion.div>

        {(isGenerating || generationFailed) && (
          <div
            className={`mb-6 rounded-lg p-4 flex items-start gap-3 ${
              generationFailed
                ? 'bg-danger-500/10 border border-danger-500/30'
                : 'bg-warning-500/10 border border-warning-500/30'
            }`}
          >
            {generationFailed
              ? <AlertTriangle className="w-5 h-5 text-danger-500 shrink-0 mt-0.5" />
              : <Loader className="w-5 h-5 text-warning-500 shrink-0 mt-0.5 animate-spin" />}
            <div className="flex-1">
              <p className={`text-sm font-medium ${generationFailed ? 'text-danger-500' : 'text-warning-600 dark:text-warning-400'}`}>
                {generationFailed
                  ? 'Survey setup failed for this participant.'
                  : 'Survey setup is in progress.'}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                {generationFailed
                  ? 'Some survey windows could not be generated. Assessment tools are limited until this is resolved.'
                  : 'We’re generating this participant’s survey windows. Assessment tools will become available once setup finishes.'}
              </p>
            </div>
            {canMarkGenerationComplete && (
              <button
                onClick={handleMarkGenerationComplete}
                disabled={markingComplete}
                className="shrink-0 inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {markingComplete ? 'Marking…' : 'Mark complete'}
              </button>
            )}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900/20 rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
            <h2 className="text-xl font-semibold text-white">
              Participant Details
            </h2>
          </div>
          <div className="p-6">
            <Tab.Group selectedIndex={selectedTab} onChange={updateActiveTab}>
              <Tab.List className="flex space-x-4 border-b border-gray-200 dark:border-gray-700 mb-6">
                {allTabs.map((tab, index) => {
                  // FECOA-1609: the "All Assessments" tab reads survey windows that
                  // don't exist yet while generation is in progress/failed — gate it.
                  const tabDisabled = tab.id === 'all-surveys' && (isGenerating || generationFailed);
                  return (
                    <Tab
                      key={tab.id}
                      disabled={tabDisabled}
                      title={tabDisabled ? 'Available once survey setup completes' : undefined}
                      className={({ selected }) =>
                        `px-4 py-2 text-sm font-medium leading-5 text-primary-700 focus:outline-none ${
                          tabDisabled
                            ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                            : selected
                            ? "border-b-2 border-primary-600"
                            : "text-gray-500 dark:text-gray-400 hover:text-primary-600"
                        }`
                      }
                    >
                      {tab.label}
                    </Tab>
                  );
                })}
              </Tab.List>
              <Tab.Panels>
                <Tab.Panel>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="grid grid-cols-3 gap-8 mb-8">
                      <div className="flex items-center space-x-3 py-2">
                        <User className="w-5 h-5 text-primary-600" />
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Participant ID:</span>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {participantDetails?.participant_id || participantDetails?.id || "N/A"}
                        </span>
                        <ParticipantActiveBadge isActive={!!participantDetails?.is_active} />
                      </div>
                      <InfoItem
                        icon={MapPin}
                        label="Site"
                        value={participantDetails?.site}
                      />
                      <InfoItem
                        icon={Calendar}
                        label="Date Joined"
                        value={formatDate(participantDetails?.date_joined)}
                      />
                      <InfoItem
                        icon={Clock}
                        label="Timezone"
                        value={participantDetails?.profile?.timezone}
                      />
                      <InfoItem
                        icon={Book}
                        label="Language"
                        value={participantDetails?.language || participantDetails?.profile?.language}
                      />
                      <InfoItem
                        icon={Clock}
                        label="Last Data Sent"
                        value={lastDataSend}
                      />
                    </div>
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                        Survey History
                      </h3>

                      {/* Suspension Notice Banner */}
                      {participantDetails && !participantDetails.is_active && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <p className="text-sm text-blue-800 text-center">
                            Surveys are temporarily suspended while the participant is inactive.
                          </p>
                        </div>
                      )}

                      <div className="space-y-4">

                        {/* MARK: Daily Surveys Table */}
                        {/* Daily Surveys Section */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 overflow-hidden">
                          <button
                            onClick={() => toggleSection('dailySurveys')}
                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="bg-blue-50 p-2 rounded-full">
                                <CalendarIcon className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100">Daily Assessments</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {Array.isArray(organizedSurveys.dailySurveys) && 
                                    `${organizedSurveys.dailySurveys.filter(s => s.status === "completed").length} of ${organizedSurveys.dailySurveys.filter(s => s.status !== "suspended").length} surveys completed`
                                  }
                                </p>
                              </div>
                            </div>
                            <ChevronDown 
                              className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
                                expandedSections.dailySurveys ? 'transform rotate-180' : ''
                              }`} 
                            />
                          </button>
                          
                          <AnimatePresence>
                            {expandedSections.dailySurveys && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden border-t border-gray-100 dark:border-gray-700"
                              >
                                <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                      <tr>
                                        {renderSortableHeader('dailySurveys', 'date', 'Date')}
                                        {renderSortableHeader('dailySurveys', 'survey_name', 'Survey')}
                                        {renderSortableHeader('dailySurveys', 'status', 'Status')}
                                        {renderSortableHeader('dailySurveys', 'completed_at', 'Completed At')}
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                      {Array.isArray(organizedSurveys.dailySurveys) && organizedSurveys.dailySurveys.length > 0 ? (
                                        getSortedSurveyRows('dailySurveys', organizedSurveys.dailySurveys).map((survey, index) => (
                                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                              {/* Show the normalized relevantDate field */}
                                              {survey.relevantDate ? formatDate(survey.relevantDate) : '—'}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-primary-600 cursor-pointer hover:text-primary-800" onClick={() => survey.object_id || survey.id ? navigateToCompletedSurvey(survey.object_id || survey.id) : null}>
                                              <div className="flex items-center">
                                                <SurveyTypeIcon type={survey.schedule_type} survey={survey} />
                                                <span className="ml-2">
                                                  {survey.survey_name}
                                                </span>
                                              </div>
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm">
                                              <SurveyStatus status={survey.status} isVisitSurvey={false} />
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                              {survey.status === 'completed' && survey.completed_at
                                                ? new Date(survey.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : '—'}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="4" className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No daily surveys found for this participant.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        
                        {/* Patient Prompted Surveys Section */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 overflow-hidden">
                          <button
                            onClick={() => toggleSection('patientPromptedSurveys')}
                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="bg-purple-50 p-2 rounded-full">
                                <Bell className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100">Patient Prompted Assessments</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {Array.isArray(organizedSurveys.patientPromptedSurveys) && 
                                    `${organizedSurveys.patientPromptedSurveys.filter(s => s.status === "completed").length} completed`
                                  }
                                </p>
                              </div>
                            </div>
                            <ChevronDown 
                              className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
                                expandedSections.patientPromptedSurveys ? 'transform rotate-180' : ''
                              }`} 
                            />
                          </button>
                          
                          <AnimatePresence>
                            {expandedSections.patientPromptedSurveys && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden border-t border-gray-100 dark:border-gray-700"
                              >
                                <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                      <tr>
                                        {renderSortableHeader('patientPromptedSurveys', 'date', 'Date')}
                                        {renderSortableHeader('patientPromptedSurveys', 'survey_name', 'Survey')}
                                        {renderSortableHeader('patientPromptedSurveys', 'status', 'Status')}
                                        {renderSortableHeader('patientPromptedSurveys', 'completed_at', 'Completed At')}
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                      {Array.isArray(organizedSurveys.patientPromptedSurveys) && organizedSurveys.patientPromptedSurveys.length > 0 ? (
                                        getSortedSurveyRows('patientPromptedSurveys', organizedSurveys.patientPromptedSurveys).map((survey, index) => (
                                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                              {survey.relevantDate ? formatDate(survey.relevantDate) : '—'}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-primary-600 cursor-pointer hover:text-primary-800" onClick={() => survey.object_id || survey.id ? navigateToCompletedSurvey(survey.object_id || survey.id) : null}>
                                              <div className="flex items-center">
                                                <SurveyTypeIcon type={survey.schedule_type} survey={survey} />
                                                <span className="ml-2">
                                                  {survey.survey_name}
                                                </span>
                                              </div>
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm">
                                              <SurveyStatus status={survey.status} isVisitSurvey={false} />
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                              {survey.status === 'completed' && survey.completed_at
                                                ? new Date(survey.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : '—'}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="4" className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No patient prompted surveys found for this participant.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        {/* MARK: Remote Surveys Table */}
                        {/* FECOA-1491: Remote (non on-site) scheduled surveys.
                            These behave like Dailies / Patient Prompted: they are
                            assigned automatically and are NOT gated by the
                            Activate/Deactivate control. */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 overflow-hidden">
                          <button
                            onClick={() => toggleSection('remoteSurveys')}
                            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                          >
                            <div className="flex items-center space-x-4">
                              <div className="bg-indigo-50 p-2 rounded-full">
                                <MapPin className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-gray-100">Remote Assessments</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {Array.isArray(organizedSurveys.remoteSurveys) &&
                                    `${organizedSurveys.remoteSurveys.filter(s => s.status === "completed").length} of ${organizedSurveys.remoteSurveys.filter(s => s.status !== "suspended").length} surveys completed`
                                  }
                                </p>
                              </div>
                            </div>
                            <ChevronDown
                              className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
                                expandedSections.remoteSurveys ? 'transform rotate-180' : ''
                              }`}
                            />
                          </button>

                          <AnimatePresence>
                            {expandedSections.remoteSurveys && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden border-t border-gray-100 dark:border-gray-700"
                              >
                                <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                      <tr>
                                        {renderSortableHeader('remoteSurveys', 'date', 'Date')}
                                        {renderSortableHeader('remoteSurveys', 'survey_name', 'Survey')}
                                        {renderSortableHeader('remoteSurveys', 'status', 'Status')}
                                        {renderSortableHeader('remoteSurveys', 'completed_at', 'Completed At')}
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                      {Array.isArray(organizedSurveys.remoteSurveys) && organizedSurveys.remoteSurveys.length > 0 ? (
                                        getSortedSurveyRows('remoteSurveys', organizedSurveys.remoteSurveys).map((survey, index) => (
                                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                              {survey.relevantDate ? formatDate(survey.relevantDate) : '—'}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-primary-600 cursor-pointer hover:text-primary-800" onClick={() => survey.object_id || survey.id ? navigateToCompletedSurvey(survey.object_id || survey.id) : null}>
                                              <div className="flex items-center">
                                                <SurveyTypeIcon type={survey.schedule_type} survey={survey} />
                                                <span className="ml-2">
                                                  {survey.survey_name}
                                                </span>
                                              </div>
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm">
                                              <SurveyStatus
                                                status={survey.status}
                                                isVisitSurvey={false}
                                                isSuspended={survey.is_suspended || (!participantDetails?.is_active && survey.status !== 'completed')}
                                              />
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                              {survey.status === 'completed' && survey.completed_at
                                                ? new Date(survey.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                : '—'}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="4" className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No remote surveys found for this participant.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        {/* MARK: Visit Surveys Table */}
                        {/* Visit Surveys Sections */}
                        {Array.isArray(organizedSurveys.visitSurveys) && organizedSurveys.visitSurveys.length > 0 ? (
                          organizedSurveys.visitSurveys.map((visit, index) => (
                            <div key={visit.id} className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 overflow-hidden">
                              <div className="flex items-center">
                                <button
                                  type="button"
                                  onClick={() => toggleSection(visit.id)}
                                  aria-expanded={!!expandedSections[visit.id]}
                                  className="min-w-0 flex-1 flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                                >
                                  <div className="flex items-center space-x-4">
                                    <div className="bg-green-50 p-2 rounded-full">
                                      <CalendarIcon className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{visit.name}</h4>
                                      <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {visit.completedCount} of {visit.totalCount} surveys completed
                                      </p>
                                    </div>
                                  </div>
                                  <ChevronDown
                                    aria-hidden="true"
                                    className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
                                      expandedSections[visit.id] ? 'transform rotate-180' : ''
                                    }`}
                                  />
                                </button>
                                <div className="flex shrink-0 items-center">
                                  {canManageVisit && (
                                    // Check if there are any activatable surveys
                                    (() => {
                                      // Get all surveys for this visit
                                      const visitSurveys = visit.surveys || [];
                                      
                                      // Check if there are any surveys that can be activated OR deactivated
                                      const hasActionableSurveys = visitSurveys.some(survey => {
                                        const status = (survey.status || '').toLowerCase();
                                        console.log(`Survey ${survey.survey_name || survey.id} status: ${status}`);
                                        
                                        // Surveys can be activated if they're 'open' or 'ready for activation'
                                        const canActivate = status === 'open' || status === 'ready for activation' || status.includes('ready');
                                        
                                        // Surveys can be deactivated if they're 'activated'
                                        const canDeactivate = status === 'activated';
                                        
                                        // Show button if survey can be either activated or deactivated
                                        const actionable = canActivate || canDeactivate;
                                        
                                        // Explicitly log which surveys are actionable
                                        if (actionable) {
                                          console.log(`Survey is ${canActivate ? 'activatable' : 'deactivatable'}: ${survey.survey_name || survey.id}`);
                                        }
                                        
                                        return actionable;
                                      });
                                      
                                      // Only show action button if there are actionable surveys (can be activated or deactivated)
                                      return hasActionableSurveys && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            console.log(`Activating visit ${visit.id} (${visit.name})`);
                                            handleVisitWindowActivation(visit.id, !visit.isActivated);
                                          }}
                                          disabled={activationLoading}
                                          className={`mr-4 px-3 py-1 text-sm rounded-full ${
                                            activationLoading
                                              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                              : visit.isActivated
                                              ? "bg-red-100 text-red-700 hover:bg-red-200"
                                              : "bg-green-100 text-green-700 hover:bg-green-200"
                                          } transition-colors duration-200 flex items-center`}
                                        >
                                          {activationLoading ? (
                                            <>
                                              <Loader className="w-3 h-3 mr-1 animate-spin" />
                                              <span>Processing...</span>
                                            </>
                                          ) : (
                                            visit.isActivated ? "Deactivate" : "Activate"
                                          )}
                                        </button>
                                      );
                                    })()
                                  )}
                                </div>
                              </div>
                              
                              <AnimatePresence>
                                {expandedSections[visit.id] && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden border-t border-gray-100 dark:border-gray-700"
                                  >
                                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                          <tr>
                                            {renderSortableHeader(`visitSurveys:${visit.id}`, 'date', 'Date')}
                                            {renderSortableHeader(`visitSurveys:${visit.id}`, 'survey_name', 'Survey')}
                                            {renderSortableHeader(`visitSurveys:${visit.id}`, 'status', 'Status')}
                                            {renderSortableHeader(`visitSurveys:${visit.id}`, 'completed_at', 'Completed At')}
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                          {visit.surveys.length > 0 ? (
                                            getSortedSurveyRows(`visitSurveys:${visit.id}`, visit.surveys).map((survey, index) => (
                                              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                  {/* Show available_at if not completed, else show completed_at */}
{survey.completed_at ? formatDate(survey.completed_at) : (survey.available_at ? formatDate(survey.available_at) : '—')}
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-primary-600 cursor-pointer hover:text-primary-800" onClick={() => navigateToCompletedSurvey(survey.object_id || survey.id)}>
                                                  <div className="flex items-center">
                                                    <SurveyTypeIcon type={survey.schedule_type} survey={survey} />
                                                    <span className="ml-2">
                                                      {survey.survey_name}
                                                    </span>
                                                  </div>
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-sm">
                                                  <SurveyStatus 
                                                    status={survey.status} 
                                                    isVisitSurvey={true} 
                                                    isSuspended={survey.is_suspended || (!participantDetails?.is_active && survey.status !== 'completed')}
                                                  />
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                  {survey.status === 'completed' && survey.completed_at
                                                    ? new Date(survey.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                    : '—'}
                                                </td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr>
                                              <td colSpan="4" className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                                No surveys found for this visit.
                                              </td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))
                        ) : (
                          <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 overflow-hidden">
                            <button
                              onClick={() => toggleSection('visitSurveys')}
                              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                            >
                              <div className="flex items-center space-x-4">
                                <div className="bg-green-50 p-2 rounded-full">
                                  <CalendarIcon className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Visit Surveys</h4>
                                  {/* FECOA-1645/1757: explain *why* the list is empty. Match the
                                      All Assessments tab wording so QA sees a consistent message
                                      regardless of which view they land on. */}
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {isGenerating
                                      ? 'Survey windows are still being generated for this participant. They will appear here once setup finishes.'
                                      : generationFailed
                                        ? 'Survey setup failed, so no survey windows were generated. See the alert above to retry, or contact support if this continues.'
                                        : 'No surveys are scheduled for this participant yet.'}
                                  </p>
                                </div>
                              </div>
                              <ChevronDown 
                                className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
                                  expandedSections.visitSurveys ? 'transform rotate-180' : ''
                                }`} 
                              />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </Tab.Panel>
                <Tab.Panel>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Data Clarification Queries
                      </h3>
                      { showExportButton && (
                      <button
                        onClick={handleExportDCQs}
                        className="flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Export DCQs
                      </button>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            {[
                              "DCQ#",
                              "Type",
                              "Action",
                              "Status",
                              "Last Updated",
                            ].map((header) => (
                              <th
                                key={header}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {dcqs.length > 0 ? (
                            dcqs.map((dcq) => (
                              <tr key={dcq.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary-600">
                                  <a
                                    href={`/studies/${participantDetails.site.study.external_id}/dcq/${dcq.id}`}
                                    className="hover:underline"
                                  >
                                    {dcq.id}
                                  </a>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {dcq.type || "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {dcq.action || "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      dcq.status === "New"
                                        ? "bg-green-100 text-green-800"
                                        : dcq.status === "Needs Clarification"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : dcq.status === "Waiting for Approval"
                                        ? "bg-blue-100 text-blue-800"
                                        : dcq.status === "Verification"
                                        ? "bg-purple-100 text-purple-800"
                                        : dcq.status === "Implementation"
                                        ? "bg-orange-100 text-orange-800"
                                        : dcq.status === "Closed"
                                        ? "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    {dcq.status || "N/A"}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {formatDate(dcq.updated_at)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                No DCQs found for this participant.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                </Tab.Panel>

                {/* MARK: All Assessments */}
                {/* Gated on is_internal — must mirror the tab-list condition
                    at line ~461 (`isAuthenticated && user?.is_internal`) or
                    the Tab.Panels index falls out of sync with the rendered
                    tab list (e.g. SC clicks Caregivers and sees this panel
                    instead). */}
                {isAuthenticated && user?.is_internal && (
                <Tab.Panel>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        All Assessments (Past, Current & Upcoming)
                      </h3>
                      <div>
                        <div className="flex space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <CalendarIcon className="w-3 h-3 mr-1" /> Daily
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Calendar className="w-3 h-3 mr-1" /> Scheduled
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <Bell className="w-3 h-3 mr-1" /> Patient Prompted
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* FECOA-1462: Filters bar (Status / Schedule Type / Date Range) */}
                    <div className="mb-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <div className="flex flex-wrap items-end gap-4">
                        <div className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400">
                          <Filter className="w-4 h-4 mr-2" /> Filters:
                        </div>
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
                          <select
                            value={allSurveyFilters.status}
                            onChange={(e) => setAllSurveyFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[160px]"
                          >
                            <option value="all">All Statuses</option>
                            {allSurveyStatusOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Schedule Type</label>
                          <select
                            value={allSurveyFilters.scheduleType}
                            onChange={(e) => setAllSurveyFilters(prev => ({ ...prev, scheduleType: e.target.value }))}
                            className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-[160px]"
                          >
                            <option value="all">All Types</option>
                            {allSurveyScheduleTypeOptions.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Date Range</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={allSurveyFilters.dateFrom}
                              max={allSurveyFilters.dateTo || undefined}
                              onChange={(e) => setAllSurveyFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                              className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-gray-400 text-sm">to</span>
                            <input
                              type="date"
                              value={allSurveyFilters.dateTo}
                              min={allSurveyFilters.dateFrom || undefined}
                              onChange={(e) => setAllSurveyFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                              className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                        {hasActiveAllSurveyFilters && (
                          <button
                            type="button"
                            onClick={resetAllSurveyFilters}
                            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline px-1 py-2"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            {renderSortableHeader('allSurveys', 'date', 'Date', 'px-4 py-3')}
                            {renderSortableHeader('allSurveys', 'visitName', 'Visit', 'px-4 py-3')}
                            {renderSortableHeader('allSurveys', 'timepoint_name', 'Timepoint', 'px-4 py-3')}
                            {renderSortableHeader('allSurveys', 'survey_name', 'Survey', 'px-4 py-3')}
                            {renderSortableHeader('allSurveys', 'status', 'Status', 'px-4 py-3')}
                            {renderSortableHeader('allSurveys', 'completed_at', 'Completed At', 'px-4 py-3')}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filteredAllSurveys.length > 0 ? (
                            getSortedSurveyRows('allSurveys', filteredAllSurveys).map((survey, index) => (
                              <tr 
                                key={index} 
                                className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                  survey.status === 'completed' ? 'bg-green-50' : 
                                  survey.status === 'missed' ? 'bg-red-50' : 
                                  survey.status === 'suspended' ? 'bg-gray-50 dark:bg-gray-900' :
                                  survey.status === 'Activated' ? 'bg-blue-50' : ''
                                }`}
                              >
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 cursor-pointer" 
                                    onClick={() => survey.object_id || survey.id ? navigateToCompletedSurvey(survey.object_id || survey.id) : null}>
                                  {survey.completed_at ? formatDate(survey.completed_at) : (survey.available_at ? formatDate(survey.available_at) : '—')}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {survey.visitName || 'N/A'}
                                </td>
                                {/* FECOA-1237: originating intra-day timepoint
                                    (e.g. "Morning"); blank for legacy windows. */}
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {survey.timepoint_name || '—'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-primary-600">
                                  <div className="flex items-center">
                                    <SurveyTypeIcon type={survey.schedule_type} survey={survey} />
                                    <span className="ml-2">
                                      {survey.survey_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <SurveyStatus 
                                    status={survey.status} 
                                    isSuspended={survey.is_suspended || (!participantDetails?.is_active && survey.status !== 'completed')}
                                  />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {survey.status === 'completed' && survey.completed_at
                                    ? new Date(survey.completed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : '—'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="px-4 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                {/* FECOA-1645: explain *why* the list is empty instead of a
                                    generic "no surveys." The generating/failed states reuse
                                    FECOA-1609's genStatus rollup (banner shown above). */}
                                {hasActiveAllSurveyFilters
                                  ? 'No assessments match the selected filters.'
                                  : isGenerating
                                    ? 'Survey windows are still being generated for this participant. They will appear here once setup finishes.'
                                    : generationFailed
                                      ? 'Survey setup failed, so no survey windows were generated. See the alert above to retry, or contact support if this continues.'
                                      : 'No surveys are scheduled for this participant yet.'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                </Tab.Panel>
                )}
                {/* MARK: Caregivers (FECOA-1321) */}
                {showCaregiversTab && (
                  <Tab.Panel>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CaregiversTab
                        participantId={participantID}
                        participantLabel={participantDetails?.participant_id}
                        siteId={siteId}
                        studyId={studyId}
                      />
                    </motion.div>
                  </Tab.Panel>
                )}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      </div>
      {isAuthenticated && (user?.is_superuser || (user?.role === 'Program Admin' && (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'uat'))) && (
        <div className="mb-6">
          <div className="flex flex-col space-y-4">
            <DateMocker
              onApplyFakeDate={applyFakeDate}
              bypassCache={bypassCache}
            />
            {/* MARK: Show the toggle only on the Info tab */}
            {showFutureAssessmentsToggle && (
            <div className="flex items-center bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <label htmlFor="showFutureToggle" className="flex items-center cursor-pointer">
                  <div className="mr-3">
                    <div className="font-medium text-sm text-gray-800 dark:text-gray-100">Show Future Assessments</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Toggle to display upcoming assessments</div>
                  </div>
                </label>
              </div>
              <div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    id="showFutureToggle"
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={showFutureAssessments} 
                    onChange={() => setShowFutureAssessments(prev => !prev)}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            )}
            
            {simulatedDate && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-2">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      <Calendar className="inline-block w-4 h-4 mr-1" />
                      Currently simulating date: <span className="font-bold">{simulatedDate}</span>
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      All data shown is how it would appear on this date.
                    </p>
                  </div>
                  <button
                    onClick={resetDateSimulation}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                  >
                    Reset to Current Date
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantDetails;
